import type { components } from './api-v2'
import type {
  AccessTier,
  AdminUserSort,
  NotificationHealthResponse,
  NotificationDeliveryRow,
} from './api'
import { eventsFromJson, takeLines, type WebsiteCheckEvent } from '@/lib/website-check-stream'
import type { CatalogResponse, PopularityResponse, ServiceDetailResponse } from '@/lib/third-party'
import type { V2RegionHold } from '@/lib/region-holds'
import type { LocationHistory } from '@/lib/location-history'
import { reportUnauthorized } from '@/lib/session-expiry'

/**
 * Typed AUTHED client for the org-scoped v2 reads served by the Rust backend
 * (uptrack-server). Auth is the Phoenix session cookie, mirrored from the legacy
 * client: `fetch` with `credentials: 'include'` against the same `VITE_API_URL`
 * base. Response types come from the generated OpenAPI schemas (`api-v2.ts`);
 * where the generated schema lags the Rust source, the gap is patched here (see
 * `EnrichedMonitor`) rather than by regenerating the whole client.
 *
 * Public (unauthenticated) status reads live in `status-v2.ts`; this is the
 * authenticated dashboard counterpart.
 */
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

type Schemas = components['schemas']

export class ApiV2Error extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
    /** The server's machine-readable `code` (for example `pro_required`, `free_plan_full`). */
    public code?: string,
    /** A 422 `errors` object: the field key of the first message (for example `third_party.component_ids`). */
    public field?: string,
    /** A 422 `errors` object: every field and its messages. */
    public fields?: Record<string, string[]>,
  ) {
    super(message)
    this.name = 'ApiV2Error'
  }
}

async function authedJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    credentials: 'include', // send the session cookie
  })
  if (res.status === 204) return undefined as T
  if (!res.ok) {
    // A 401 on a signed-in page means the session ended: sign in again, once, and come back here.
    if (res.status === 401) reportUnauthorized(path)
    throw await apiErrorFrom(res)
  }
  return (await res.json()) as T
}

/** A JSON POST body for `authedJson`. */
function jsonPost(body: unknown): RequestInit {
  return { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
}

const FIELD_LABELS: Record<string, string> = {
  'third_party.service_slug': 'Service',
  'third_party.component_ids': 'Components',
  third_party: 'Third party',
  monitor_type: 'Monitor type',
  url: 'URL',
  regions_required: 'Number of regions required',
}

/** "third_party.component_ids" -> "Components". An unknown key reads as its last part in words. */
function fieldLabel(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key]
  const last = key.split('.').pop() ?? key
  const words = last.replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key
}

/** The 422 body `{"errors":{"field":["message"]}}`: only keys with at least one text message. */
function fieldErrorsFrom(value: unknown): Record<string, string[]> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const out: Record<string, string[]> = {}
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const list = (Array.isArray(raw) ? raw : [raw]).filter((m): m is string => typeof m === 'string' && m.trim() !== '')
    if (list.length > 0) out[key] = list
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/** The `ApiV2Error` for a failed response, with the server's message when it sent one. */
async function apiErrorFrom(res: Response): Promise<ApiV2Error> {
  let message = res.status === 401 ? 'Unauthorized' : `request failed: ${res.status}`
  let details: unknown
  let code: string | undefined
  let field: string | undefined
  let fields: Record<string, string[]> | undefined
  try {
    const body = (await res.json()) as {
      error?: { message?: string; details?: unknown; code?: string } | string
      code?: string
      message?: string
      retry_after_secs?: number
      /** `reauth_required`: which sign-in method the user must repeat. */
      method?: string
      /** 422: `{"errors":{"third_party.service_slug":["is not a service Uptrack can watch"]}}`. */
      errors?: unknown
    }
    if (typeof body?.code === 'string') code = body.code
    else if (typeof body?.error === 'object' && typeof body.error?.code === 'string') code = body.error.code
    const err = body?.error
    if (typeof err === 'string') message = err
    else if (err?.message) {
      message = err.message
      details = err.details
    } else if (body?.message) message = body.message
    else {
      fields = fieldErrorsFrom(body?.errors)
      const first = fields && Object.entries(fields)[0]
      if (first) {
        field = first[0]
        message = `${fieldLabel(first[0])} ${first[1][0]}`
      }
    }
    // Some endpoints (e.g. the rate-limited preview tool) return a top-level
    // retry hint alongside a machine-code error — keep it for callers.
    if (details === undefined && typeof body?.retry_after_secs === 'number') {
      details = { retry_after_secs: body.retry_after_secs }
    }
    if (details === undefined && typeof body?.method === 'string') {
      details = { method: body.method }
    }
  } catch {
    /* non-JSON body — keep the default message */
  }
  // A 429 names its wait in the Retry-After header (whole seconds). A body hint wins.
  if (res.status === 429 && !(details && typeof details === 'object' && 'retry_after_secs' in details)) {
    const secs = Number(res.headers?.get?.('Retry-After'))
    if (Number.isFinite(secs) && secs > 0) details = { ...(details as object | undefined), retry_after_secs: secs }
  }
  return new ApiV2Error(res.status, message, details, code, field, fields)
}

/**
 * A monitor as returned by the list/detail endpoints — including the VM-derived
 * `last_check` + `uptime_percentage` (now part of the regenerated `MonitorOut`).
 */
export type EnrichedMonitor = Schemas['MonitorOut'] & {
  /** Set while the monitor is in the early-warning degraded state
   *  (grow-browser-testing-ux). Drives the amber list badge. */
  degraded_since?: string | null
}

/** `POST /api/billing/promo/validate` 200. `summary` is the server's sentence; the web builds its own from the numbers. */
export interface PromoValidation {
  kind: 'discount'
  percent_off: number
  duration_intervals: number | null
  plan: string
  interval: 'monthly' | 'yearly'
  summary?: string
}

/** `POST /api/billing/redeem` 200. */
export interface RedeemResult {
  kind: 'grant'
  plan: string
  ends_at: string
  already_redeemed: boolean
}

/** One-off browser preview result (public tool / new-monitor "Test now"). */
export interface BrowserPreviewResult {
  status: string
  duration_ms: number
  screenshot_url: string | null
  web_vitals: {
    lcp_ms: number | null
    cls: number | null
    fcp_ms: number | null
    ttfb_ms: number | null
    inp_ms: number | null
  } | null
  /** Why the check is `down`: one cleaned line from the browser. Absent when up, or when the probe cannot say. */
  error?: string
}

/** One point on the Web Vitals trend (browser monitor page). cls is unitless;
 *  the _ms metrics are milliseconds. Any may be null (unmeasured/capture off). */
export interface VitalsPoint {
  inserted_at: string
  lcp_ms: number | null
  cls: number | null
  fcp_ms: number | null
  ttfb_ms: number | null
  inp_ms: number | null
}

export interface MonitorsListMeta {
  total: number
  page: number
  per_page: number
  /** Derived client-side; the v2 `meta` carries only total/page/per_page. */
  total_pages: number
}

export interface MonitorsListResult {
  data: EnrichedMonitor[]
  meta: MonitorsListMeta
}

/**
 * `config` is a free-form jsonb on the Rust side, so the generated schema types
 * it as `Record<string, never>` (no properties). Narrow it to the keys the UI
 * reads (per-channel: email / url / webhook / etc.).
 */
export type V2AlertChannel = Omit<Schemas['AlertChannelOut'], 'config'> & {
  config: { email?: string; url?: string } & Record<string, unknown>
}

export interface AlertChannelsListResult {
  data: V2AlertChannel[]
}

/** A status page in the management list (generated schema is current/correct). */
export type V2StatusPage = Schemas['StatusPageOut']
export type StatusPagesListResult = Schemas['StatusPageListResponse']

/** An incident as returned by the v2 incidents endpoints. */
export type V2Incident = Schemas['IncidentOut']

/** An escalation policy (+ its steps) as returned by the v2 endpoints. */
export type V2EscalationPolicy = Schemas['EscalationPolicyOut']
export type V2EscalationStep = Schemas['EscalationStepOut']

/** An API key (metadata only — the secret is shown once on create) as returned by v2. */
export type V2ApiKey = Schemas['ApiKeyOut']

/** An org member as returned by the v2 members endpoint. */
export type V2Member = Schemas['MemberOut']

/** A pending team invitation as returned by the v2 endpoint. */
export type V2Invitation = Schemas['InvitationOut']

/** Public invite details (by token) as returned by `GET /api/invitations/{token}`. */
export type V2PublicInvitation = Schemas['PublicInvitationOut']

/** A notification-delivery record as returned by `GET /api/notification-deliveries`. */
export type V2NotificationDelivery = Schemas['DeliveryOut']

/**
 * Public SSL checker result (`POST /api/tools/ssl-check`). serde_json handler -> hand-typed.
 * One location only (Germany). An expired or untrusted certificate is still reported (not an
 * error); `days_remaining` is negative when expired. `reachable: false` carries `error`.
 */
export type V2SslCheck = {
  host: string
  port: number
  checked_at: string
  reachable: boolean
  trusted: boolean
  /** Omitted when the certificate is trusted. */
  trust_error?: string | null
  protocol?: string | null
  cipher?: string | null
  subject?: string | null
  issuer?: string | null
  not_before?: string | null
  not_after?: string | null
  days_remaining?: number | null
  sans: string[]
  chain: Array<{ subject: string; issuer: string; not_before: string; not_after: string }>
  response_time_ms: number
  error?: string | null
}

/** TLS ports the SSL checker accepts. */
export const SSL_PORTS = [443, 465, 993, 995, 8443] as const
export type SslPort = (typeof SSL_PORTS)[number]

/** Record types the DNS lookup accepts. */
export const DNS_RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'NS', 'TXT', 'SOA', 'CAA', 'PTR'] as const
export type DnsRecordType = (typeof DNS_RECORD_TYPES)[number]

export type DnsResolverId = 'cloudflare' | 'google' | 'quad9'

/**
 * Public DNS lookup result (`POST /api/tools/dns-lookup`). The answers come from three public
 * resolvers, not from regions. `consistent` ignores resolvers that errored and is false when
 * none answered. NXDOMAIN and an empty NOERROR are valid answers (empty `answers`).
 */
export type V2DnsLookup = {
  name: string
  record_type: DnsRecordType
  checked_at: string
  consistent: boolean
  results: Array<{
    resolver: string
    label: string
    ip: string
    answers: Array<{ value: string; ttl: number; priority?: number }>
    rcode: string | null
    response_time_ms: number
    error?: string | null
  }>
}

/**
 * Public domain lookup result (`POST /api/tools/domain-lookup`). RDAP only. `registered: null`
 * means the registry returned no data: it does NOT mean the name is free.
 */
export type V2DomainLookup = {
  domain: string
  checked_at: string
  registered: boolean | null
  /** Optional fields can be missing or null; treat both the same. */
  registrar?: string | null
  created?: string | null
  updated?: string | null
  expires?: string | null
  days_remaining?: number | null
  age_days?: number | null
  status: string[]
  nameservers: string[]
  source: 'rdap'
  error?: string | null
}

/** Home-page live counters: global totals only, no organization data. */
export type PublicStats = {
  checks: { today: number; yesterday: number }
  outages: { today: number; yesterday: number }
  alerts: { today: number; yesterday: number }
  updated_at: string
}

/** Client-reported funnel steps. The server allowlist decides which it accepts. */
export type FunnelEvent = 'checkout_opened' | 'tool_check_started' | 'tool_check_result' | 'tool_cta_click'

/**
 * Public website-checker result (`POST /api/tools/check-website`). serde_json handler → hand-typed.
 * One entry per probe region; `status` is `up` | `down` | `unavailable` (the region's
 * probe did not answer — not a verdict on the site).
 */
export type V2WebsiteCheck = {
  url: string
  checked_at: string
  /** Set when the site was checked from every location moments ago and this check used the core locations only. */
  note?: string
  /** Server-side verdict over the regions that answered. */
  summary?: {
    verdict: 'up' | 'down' | 'partial' | 'unknown'
    up: number
    down: number
    unavailable: number
    answered: number
    total: number
  }
  results: Array<{
    region: string
    location: string
    status: string
    status_code?: number
    response_time?: number
    error?: string
    /** Whether the TLS certificate verified (false on a broken chain). */
    tls_verified?: boolean
    /** True when the host responded but its certificate failed verification. */
    reachable?: boolean
  }>
}

/** Options to make the pre-save Test mirror the monitor's own settings. */
export type WebsiteCheckOptions = {
  method?: string
  headers?: Record<string, string>
  expected_status_codes?: number[]
  verify_ssl?: boolean
}

/** Smart-defaults preview for a URL (`POST /api/monitors/smart-defaults`). serde_json handler → hand-typed. */
export type V2SmartDefaults = {
  name: string
  url: string
  monitor_type: string
  settings: Record<string, unknown>
  interval: number
  timeout: number
  suggested_regions: string[]
}

/** A maintenance window as returned by the v2 endpoints. */
export type V2MaintenanceWindow = Schemas['MaintenanceWindowOut']

/** An audit-log entry (+ its actor) as returned by the v2 endpoint. */
export type V2AuditLog = Schemas['AuditLogOut']

/** Dashboard summary counts as returned by the v2 endpoint. */
export type V2DashboardStats = Schemas['DashboardStats']

/**
 * A recent check point. `response_headers` is free-form jsonb (typed
 * `Record<string, never>` by the schema); narrow to a string map so the SSL
 * panel (`.ssl_*`) and header lookups (`['content-length']`) read cleanly.
 * Present only on DOWN checks (from the nearest failure row); null otherwise.
 */
export type V2Check = Omit<Schemas['CheckOut'], 'response_headers'> & {
  response_headers: Record<string, string> | null
}

/**
 * A monitor's analytics: uptime series (`{date, uptime}`), response-time series
 * (`{timestamp, response_time}`), latency percentiles and incident stats — the
 * shape the monitor-detail charts consume. `uptime_chart` is date-keyed (not the
 * legacy unix-ts), matching `UptimeChart`/`UptimeBar`.
 */
export type V2MonitorAnalytics = Schemas['MonitorAnalyticsOut'] & {
  /** The first sample in the period, in unix seconds. Absent on an older server. */
  data_since?: number | null
}

/** Org dashboard analytics: overview counts + overall uptime + daily uptime trend. */
export type V2DashboardAnalytics = Schemas['DashboardAnalyticsOut']

/** Org trends: overall uptime + uptime trend + incident frequency + top offenders. */
export type V2OrgTrends = Schemas['OrgTrendsOut']

/** The org's active subscription (Paddle or App Store), or null — id/plan/status/period/cancelled_at. */
export type V2Subscription = Schemas['SubscriptionOut'] & {
  /** When a cancel scheduled at period end takes effect (openspec 112). */
  scheduled_cancel_at?: string | null
  /** Which store bills it: `apple` for a plan bought in the iOS app (In-App Purchase). */
  provider?: 'paddle' | 'apple' | null
  apple_product_id?: string | null
}
/** A live nonprofit grant, as `GET /api/billing/subscription` reports it (OpenSpec 136). */
export type V2SubscriptionGrant = Omit<Schemas['GrantOut'], 'source'> & { source: NonprofitGrantSource }
export type V2SubscriptionResponse = Omit<Schemas['SubscriptionResponse'], 'data' | 'grant'> & {
  data?: V2Subscription | null
  /** A live nonprofit grant gives Pro without a subscription (OpenSpec 136). Absent on older API builds. */
  grant?: V2SubscriptionGrant | null
}

// ── Nonprofit program (OpenSpec 136). The owner endpoints come from the generated schemas, with
// `source` and `status` narrowed (the server types them as strings). The admin endpoints are not in
// the public OpenAPI file, so their extra fields stay hand-written. ──

/** Where a grant came from: the `.ngo`/`.ong` TLD, a list, an approved application, or an admin. */
export type NonprofitGrantSource = 'tld' | 'curated' | 'uk_charity_commission' | 'acnc' | 'manual' | 'admin'
/** What the server says about one of the organization's email domains. */
export type NonprofitVerdict = Schemas['Verdict']
/** The sources a whitelisted domain row can have (no `tld` or `admin`: those are not rows). */
export type NonprofitDomainSource = 'curated' | 'uk_charity_commission' | 'acnc' | 'manual'

export type NonprofitGrant = Omit<Schemas['Grant'], 'source' | 'status'> & {
  source: NonprofitGrantSource
  status: 'active' | 'ended' | 'revoked'
}

export type NonprofitApplication = Omit<Schemas['Application'], 'register_url' | 'status' | 'decision_note' | 'decided_at'> & {
  register_url: string | null
  status: 'pending' | 'approved' | 'rejected'
  decision_note: string | null
  decided_at: string | null
}

export type NonprofitDomainVerdict = Omit<Schemas['DomainVerdict'], 'source'> & { source: string | null }

/** `GET /api/nonprofit/status`: any member may read it; `can_manage` is owner or admin. */
export type NonprofitStatus = Omit<Schemas['StatusResponse'], 'grant' | 'application' | 'domains'> & {
  grant: NonprofitGrant | null
  application: NonprofitApplication | null
  domains: NonprofitDomainVerdict[]
}

export type NonprofitApplicationInput = Omit<Schemas['ApplicationInput'], 'register_url'> & { register_url?: string }

export type AdminNonprofitApplication = NonprofitApplication & {
  organization_id: string
  organization_name: string
  user_email: string | null
}
export type AdminNonprofitGrant = NonprofitGrant & { organization_id: string; organization_name: string; note: string | null }
export type AdminNonprofitDomain = {
  domain: string
  source: NonprofitDomainSource
  registry_id: string | null
  org_name: string | null
  note: string | null
  inserted_at: string
}
export type AdminNonprofitBlock = { domain: string; reason: string; inserted_at: string }
export type AdminNonprofitImport = {
  source: 'uk_charity_commission' | 'acnc'
  started_at: string | null
  succeeded_at: string | null
  rows_seen: number | null
  domains: number | null
  last_error: string | null
}

/** Billing add-ons: configured add-ons + monthly cost + catalog of available types. */
export type V2AddOnsResponse = Schemas['AddOnsResponse']

/** Downgrade-to-free preview: current count, free limit, and the monitors that would be paused. */
export type V2DowngradePreview = Schemas['DowngradePreviewOut']

/** Two-factor (TOTP) status for the current user — { enabled }. */
export type V2TwoFactorStatus = Schemas['TwoFactorStatus']

/** SSO/SAML provider config (or null) — entity_id/sso_url/enforce/is_active/has_certificate. */
export type V2SsoConfig = Schemas['SsoConfig']
export type V2SsoConfigResponse = Schemas['SsoConfigResponse']

/** The org's custom email sender (or null) — sender_name/sender_email/verified. */
export type V2CustomSenderData = Schemas['CustomSenderData']
export type V2CustomSenderResponse = Schemas['CustomSenderResponse']

/** An OAuth client in the management list (no secret) — id/name/redirect_uris/inserted_at. */
export type V2OAuthClient = Schemas['OAuthClientOut']
export type V2OAuthClientsResponse = Schemas['OAuthClientsResponse']
/** The create-OAuth-client response — carries the plaintext client_secret (shown once). */
export type V2CreateOAuthClientOut = Schemas['CreateOAuthClientOut']

/**
 * Create/update bodies for a monitor. `settings` is free-form jsonb, typed
 * `Record<string, never>` by the generated schema; widen it so callers can pass
 * real per-type settings (same hand-override as the read-side V2 types).
 */
export type V2CreateMonitorBody = Omit<Schemas['CreateMonitor'], 'settings'> & {
  settings?: Record<string, unknown> | null
}
export type V2UpdateMonitorBody = Omit<Schemas['UpdateMonitor'], 'settings'> & {
  settings?: Record<string, unknown> | null
}

/**
 * Create/update bodies for an alert channel. `config` is free-form jsonb (typed
 * `Record<string, never>` by the schema); widen it to the per-channel keys the
 * UI sends (email / url / webhook / etc).
 */
export type V2CreateAlertChannelBody = Omit<Schemas['CreateAlertChannel'], 'config'> & {
  config?: Record<string, unknown> | null
}
export type V2UpdateAlertChannelBody = Omit<Schemas['UpdateAlertChannel'], 'config'> & {
  config?: Record<string, unknown> | null
}

/**
 * Create/update bodies for a status page. `theme_config` is free-form jsonb
 * (typed `Record<string, never>`); widen it so theme settings can be passed.
 */
export type V2CreateStatusPageBody = Omit<Schemas['CreateStatusPage'], 'theme_config'> & {
  theme_config?: Record<string, unknown> | null
}
export type V2UpdateStatusPageBody = Omit<Schemas['UpdateStatusPage'], 'theme_config'> & {
  theme_config?: Record<string, unknown> | null
}

/** A step in an escalation policy's body (steps are replaced wholesale on write).
 *  Exactly one of `alert_channel_id` / `on_call_schedule_id` per step. */
export type V2EscalationStepInput = {
  alert_channel_id?: string
  on_call_schedule_id?: string
  step_order?: number
  delay_minutes?: number
}
/** Create/update body for an escalation policy (the request body isn't a generated schema). */
export type V2EscalationPolicyBody = {
  name?: string
  description?: string
  steps?: V2EscalationStepInput[]
}

/** On-call rotation participant: who + the channels to page them on. */
export interface V2OnCallParticipant {
  id: string
  channel_ids: string[]
}
/** An on-call schedule (rotation). */
export interface V2OnCallSchedule {
  id: string
  organization_id: string
  name: string
  timezone: string
  rotation_type: string
  rotation_length_secs: number
  anchor_at: string
  participants: V2OnCallParticipant[]
  inserted_at: string
  updated_at: string
}
/** Resolved on-call (from /current or /next): who, and when the shift ends / next handoff. */
export interface V2OnCallResolved {
  participant: V2OnCallParticipant | null
  at: string | null
}
/** Create body for an on-call schedule. */
export type V2OnCallScheduleBody = {
  name: string
  timezone: string
  rotation_type: 'daily' | 'weekly' | 'custom'
  rotation_length_secs?: number
  anchor_at: string
  participants: V2OnCallParticipant[]
}

/** Create body for a maintenance window (ISO start_time/end_time; optional single monitor). */
export type V2CreateMaintenanceWindowBody = {
  title: string
  description?: string
  start_time: string
  end_time: string
  recurrence?: string
  monitor_id?: string
}
/** Update body for a maintenance window (all optional; `status` drives the "complete" action). */
export type V2UpdateMaintenanceWindowBody = Partial<{
  title: string
  description: string
  start_time: string
  end_time: string
  recurrence: string
  status: string
}>

/** The one-time create-API-key response — carries the plaintext `raw_key` (shown once). */
export type V2CreateApiKeyResponse = {
  api_key: V2ApiKey
  raw_key: string
  message: string
}

/** The incident detail envelope ({ data: { incident, updates } }) returned by the incident writes. */
export type V2IncidentDetailResponse = Schemas['IncidentDetailResponse']

/** The current user as returned by login/register/me (notification_preferences is not included). */
export type V2AuthUser = {
  id: string
  name: string
  email: string
  provider: string | null
  role: string
  is_admin: boolean
  /** False until the email address is confirmed. Absent on older API builds. */
  email_confirmed?: boolean
  /** False for an account made with an email link or a provider: it has no password to send. Absent on older API builds. */
  has_password?: boolean
  preferred_locale: string | null
  inserted_at: string
}
/** The org as returned by login/register/me. */
export type V2AuthOrg = { features_enabled?: boolean; tier?: AccessTier; id: string; name: string; slug: string; plan: string }
/** A successful login/register — user + organization. */
export type V2AuthResponse = { user: V2AuthUser; organization: V2AuthOrg }
/** Login may require a second factor before issuing the session. */
export type V2TotpRequired = { totp_required: true }

/**
 * `GET /api/auth/me` — current user + org. The Rust endpoint omits the
 * impersonation fields (admin feature, separate); they're typed optional here so
 * the provider's defensive reads stay clean (they resolve to undefined → null).
 */
export type V2CurrentUserResponse = V2AuthResponse & {
  impersonating_admin?: { id: string; name: string; email: string }
  impersonation_started_at?: string
  impersonation_expires_at?: string
}

/** A browser-check run row (list). Mirrors the server's BrowserRunOut. */
export interface V2BrowserRun {
  check_run_id: string
  status: string
  step_count: number
  failed_step_index: number | null
  duration_ms: number
  ssim: number | null
  visual_passed: boolean | null
  baseline_seeded: boolean | null
  inserted_at: string
  /** Presigned thumbnail (15-min); null when R2 is unconfigured. */
  screenshot_url: string | null
}

/** `GET /api/monitors/{id}/regions` — one region's latest verdict. */
export interface V2RegionVerdict {
  region: string
  /** Display name when the API knows one ("Nuremberg (free)" for the free location). */
  label?: string
  /** "up" | "down" */
  status: string
  response_time: number
  /** When this location last checked. Absent on an older server. */
  checked_at?: string
  /** No check in the last 2 intervals plus 5 minutes. Absent means recent. */
  stale?: boolean
}

/** Per-region consensus: each region's verdict + the rule + the resulting decision. */
export interface V2MonitorConsensus {
  monitor_id: string
  /** "majority" | "all" | "any" | "<n>" */
  rule: string
  regions: V2RegionVerdict[]
  down_count: number
  total: number
  /** "up" | "down", or null when no region has reported yet. */
  verdict: string | null
  /** Edge cities held off for this monitor right now. Absent on an older server. */
  holds?: V2RegionHold[]
  /** The latest answer of each Cloudflare city. Not part of `down_count` and `total`. */
  cities?: V2RegionVerdict[]
}

export interface V2LocationHistoryResponse {
  monitor_id: string
  period_days: number
  step_seconds: number
  locations: LocationHistory[]
}

/** One step of a browser run (replay timeline). */
export interface V2BrowserStep {
  index: number
  name: string
  ok: boolean
  duration_ms: number
  error: string | null
}

/** This month's browser-run usage + plan ceiling. Mirrors BrowserUsageOut.
 * `estimate` is null for unlimited/fair-use (Business). */
export interface V2BrowserUsage {
  plan: string
  month_runs: number
  estimate: number | null
}

/** Browser-run detail + presigned artifact URLs. Mirrors BrowserRunDetailOut. */
export interface V2BrowserRunDetail extends V2BrowserRun {
  region: string
  error: string | null
  baseline_version: number | null
  tarball_url: string | null
  baseline_url: string | null
  /** Visual-regression diff overlay; present only on a regression (visual_passed===false). */
  diff_url: string | null
  steps: V2BrowserStep[] | null
  // Core Web Vitals (add-browser-check-artifacts). Null when not captured /
  // unmeasured / pre-migration. cls is unitless; the rest are milliseconds.
  lcp_ms: number | null
  cls: number | null
  fcp_ms: number | null
  ttfb_ms: number | null
  inp_ms: number | null
  /** Presigned Playwright trace.zip (every run when captured). */
  trace_url: string | null
  /** Presigned failure video.webm (failure-only); null on passing runs. */
  video_url: string | null
  /** Captured console messages (capped); empty pre-diagnostics. */
  console?: V2ConsoleEntry[]
  /** Captured network requests (capped); empty pre-diagnostics. */
  network?: V2NetworkEntry[]
}

/** One console message from a browser run. */
export interface V2ConsoleEntry {
  level: string
  text: string
  ts?: string
}
/** One network request from a browser run. */
export interface V2NetworkEntry {
  url: string
  type: string
  status: number
  size: number
  duration_ms: number
}

export const apiV2 = {
  /** `GET /api/monitors` — paginated, org-scoped, with VM enrichment. */
  listMonitors: async (params?: {
    page?: number
    per_page?: number
    search?: string
    /** Split-nav section: 'browser' (browser checks), 'uptime' (everything else, third party included, D12)
     *  or 'third_party' (third party monitors only). */
    category?: 'browser' | 'uptime' | 'third_party'
  }): Promise<MonitorsListResult> => {
    const q = new URLSearchParams()
    if (params?.page) q.set('page', String(params.page))
    if (params?.per_page) q.set('per_page', String(params.per_page))
    if (params?.search) q.set('search', params.search)
    if (params?.category) q.set('category', params.category)
    const qs = q.toString()
    const res = await authedJson<{
      data: EnrichedMonitor[]
      meta: { total: number; page: number; per_page: number }
    }>(`/api/monitors${qs ? `?${qs}` : ''}`)
    const per_page = res.meta.per_page || 1
    return {
      data: res.data,
      meta: { ...res.meta, total_pages: Math.max(1, Math.ceil(res.meta.total / per_page)) },
    }
  },

  /**
   * `GET /api/third-party/services` — the public catalog (OpenSpec 128): groups, services, status and
   * the real `service_count`. No session cookie; cached 60 s on the server. An empty 404 means the
   * feature is switched off.
   */
  getThirdPartyCatalog: async (signal?: AbortSignal): Promise<CatalogResponse> => {
    const res = await fetch(`${API_URL}/api/third-party/services`, { headers: { Accept: 'application/json' }, signal })
    if (!res.ok) throw await apiErrorFrom(res)
    return (await res.json()) as CatalogResponse
  },

  /**
   * `GET /api/third-party/popularity` — which services customers watch most, from Uptrack's own data.
   * Public, no session cookie. Below the thresholds the body is `{ data: { visible: false } }`.
   */
  getThirdPartyPopularity: async (signal?: AbortSignal): Promise<PopularityResponse> => {
    const res = await fetch(`${API_URL}/api/third-party/popularity`, { headers: { Accept: 'application/json' }, signal })
    if (!res.ok) throw await apiErrorFrom(res)
    return (await res.json()) as PopularityResponse
  },

  /** `GET /api/third-party/services/{slug}` — components, vendor incidents and allowance (signed in). */
  getThirdPartyService: (slug: string, signal?: AbortSignal) =>
    authedJson<ServiceDetailResponse>(`/api/third-party/services/${encodeURIComponent(slug)}`, { signal }),

  /** `GET /api/monitors/{id}` — a single monitor (VM-enriched), org-scoped. */
  getMonitor: (id: string) =>
    authedJson<{ data: EnrichedMonitor }>(`/api/monitors/${encodeURIComponent(id)}`),

  /** `GET /api/monitors/{id}/incidents` — the latest incidents for one monitor. */
  getMonitorIncidents: (id: string) =>
    authedJson<{ data: Schemas['IncidentOut'][] }>(
      `/api/monitors/${encodeURIComponent(id)}/incidents`,
    ),

  /** `GET /api/monitors/{id}/regions` — per-region verdicts + consensus decision. */
  /** The latest checks of a page of monitors (at most 100), in one request. */
  getMonitorsRegions: (ids: readonly string[]) =>
    authedJson<{ data: V2MonitorConsensus[] }>(
      `/api/monitors/regions?ids=${ids.map(encodeURIComponent).join(',')}`,
    ),

  getMonitorRegions: (id: string) =>
    authedJson<{ data: V2MonitorConsensus }>(
      `/api/monitors/${encodeURIComponent(id)}/regions`,
    ),

  /** `GET /api/incidents` — the org's incidents, newest first (optional `?status=`). */
  listIncidents: (status?: string) =>
    authedJson<{ data: Schemas['IncidentOut'][] }>(
      `/api/incidents${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),

  /** `GET /api/incidents/{id}` — one incident + its updates ({ data: { incident, updates } }). */
  getIncident: (id: string) =>
    authedJson<Schemas['IncidentDetailResponse']>(`/api/incidents/${encodeURIComponent(id)}`),

  /** `GET /api/alert-channels` — the org's alert channels (data envelope). */
  listAlertChannels: () => authedJson<AlertChannelsListResult>('/api/alert-channels'),

  /** `GET /api/status-pages` — the org's status pages, by name (data envelope). */
  listStatusPages: () => authedJson<StatusPagesListResult>('/api/status-pages'),

  /** `GET /api/status-pages/{id}` — one status page + its monitors ({ data: { page, monitors } }). */
  getStatusPage: (id: string) =>
    authedJson<Schemas['StatusPageDetailResponse']>(`/api/status-pages/${encodeURIComponent(id)}`),

  /** `GET /api/escalation-policies` — the org's escalation policies (+ steps). */
  listEscalationPolicies: () =>
    authedJson<{ data: V2EscalationPolicy[] }>('/api/escalation-policies'),

  /** `GET /api/api-keys` — the org's API keys (metadata; data envelope). */
  listApiKeys: () => authedJson<{ data: V2ApiKey[] }>('/api/api-keys'),

  /** `GET /api/members` — the authed org's members (org from session; data envelope). */
  listMembers: () => authedJson<{ data: V2Member[] }>('/api/members'),

  /** `GET /api/invitations` — the org's pending invitations (session-scoped; data envelope). */
  listInvitations: () => authedJson<{ data: V2Invitation[] }>('/api/invitations'),

  /** `GET /api/maintenance-windows` — the org's maintenance windows (data envelope). */
  listMaintenanceWindows: () =>
    authedJson<{ data: V2MaintenanceWindow[] }>('/api/maintenance-windows'),

  /** `GET /api/audit-logs` — the org's audit log (session-scoped; data + meta.total). */
  listAuditLogs: (params?: {
    limit?: number
    offset?: number
    action?: string
    user_id?: string
    since?: string
  }) => {
    const q = new URLSearchParams()
    if (params?.limit != null) q.set('limit', String(params.limit))
    if (params?.offset != null) q.set('offset', String(params.offset))
    if (params?.action) q.set('action', params.action)
    if (params?.user_id) q.set('user_id', params.user_id)
    if (params?.since) q.set('since', params.since)
    const qs = q.toString()
    return authedJson<{ data: V2AuditLog[]; meta: { total: number } }>(
      `/api/audit-logs${qs ? `?${qs}` : ''}`,
    )
  },

  /** `GET /api/dashboard/stats` — org summary counts (data envelope). */
  getDashboardStats: () => authedJson<{ data: V2DashboardStats }>('/api/dashboard/stats'),

  /** `GET /api/monitors/{id}/checks?limit=N` — recent check points (VM + failure enrichment). */
  getMonitorChecks: (id: string, limit?: number) =>
    authedJson<{ data: V2Check[] }>(
      `/api/monitors/${encodeURIComponent(id)}/checks${limit != null ? `?limit=${limit}` : ''}`,
    ),

  /** `GET /api/monitors/{id}/browser-runs` — paginated browser runs, newest first. */
  listBrowserRuns: (monitorId: string, page?: number, perPage?: number) => {
    const q = new URLSearchParams()
    if (page != null) q.set('page', String(page))
    if (perPage != null) q.set('per_page', String(perPage))
    const qs = q.toString()
    return authedJson<{ data: V2BrowserRun[] }>(
      `/api/monitors/${encodeURIComponent(monitorId)}/browser-runs${qs ? `?${qs}` : ''}`,
    )
  },

  /** `GET /api/monitors/{id}/browser-runs/{run_id}` — run detail + presigned artifact URLs. */
  getBrowserRun: (monitorId: string, runId: string) =>
    authedJson<{ data: V2BrowserRunDetail }>(
      `/api/monitors/${encodeURIComponent(monitorId)}/browser-runs/${encodeURIComponent(runId)}`,
    ),

  /** `POST /api/monitors/{id}/browser-runs/run-now` — queue a one-off browser check. */
  runBrowserCheckNow: (monitorId: string) =>
    authedJson<{ status: string }>(
      `/api/monitors/${encodeURIComponent(monitorId)}/browser-runs/run-now`,
      { method: 'POST' },
    ),

  /** `GET /api/usage/browser-runs` — this month's browser-run count + plan ceiling. */
  getBrowserUsage: () => authedJson<{ data: V2BrowserUsage }>(`/api/usage/browser-runs`),

  /** `GET /api/monitors/{id}/browser-runs/vitals?days=N` — LCP/CLS trend. */
  getBrowserVitalsSeries: (monitorId: string, days?: number) =>
    authedJson<{ data: VitalsPoint[] }>(
      `/api/monitors/${encodeURIComponent(monitorId)}/browser-runs/vitals${days != null ? `?days=${days}` : ''}`,
    ),

  /** `POST /api/tools/browser-preview` — one-off, monitor-less browser check
   *  (public; no auth). Returns a screenshot URL + Web Vitals. Spins up a real
   *  Chrome, so it's rate-limited (429 `rate_limited`) and capped (`busy`); we
   *  translate those machine codes into a human message for the toast. */
  browserPreview: async (url: string, script?: string): Promise<BrowserPreviewResult> => {
    try {
      // `script` is only honored server-side for an authenticated request; the
      // public tool sends none and gets a fixed page-load.
      const body = script && script.trim() ? { url, script } : { url }
      return await authedJson<BrowserPreviewResult>('/api/tools/browser-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
    } catch (e) {
      if (e instanceof ApiV2Error) {
        const retry = (e.details as { retry_after_secs?: number } | undefined)?.retry_after_secs
        if (e.message === 'rate_limited' || e.status === 429) {
          throw new ApiV2Error(
            e.status,
            `The one-off browser test runs a real Chrome, so it's limited to one run per minute. Please wait ${
              retry ? `${retry} second${retry === 1 ? '' : 's'}` : 'about a minute'
            } and try again — or just save the monitor and use “Run now” on its page.`,
            e.details,
          )
        }
        if (e.message === 'busy') {
          throw new ApiV2Error(
            e.status,
            'All browser test runners are busy right now. Give it a few seconds and try again.',
            e.details,
          )
        }
      }
      throw e
    }
  },

  /** `GET /api/analytics/monitors/{id}?days=N` — uptime/response-time series + percentiles + incident stats (no envelope). */
  getMonitorLocationHistory: (id: string, days: number) =>
    authedJson<V2LocationHistoryResponse>(
      `/api/analytics/monitors/${encodeURIComponent(id)}/locations?days=${days}`,
    ),

  getMonitorAnalytics: (id: string, days?: number) =>
    authedJson<V2MonitorAnalytics>(
      `/api/analytics/monitors/${encodeURIComponent(id)}${days != null ? `?days=${days}` : ''}`,
    ),

  /** `GET /api/analytics/dashboard?days=N` — org overview counts + overall uptime + daily uptime trend (no envelope). */
  getDashboardAnalytics: (days?: number) =>
    authedJson<V2DashboardAnalytics>(
      `/api/analytics/dashboard${days != null ? `?days=${days}` : ''}`,
    ),

  /** `GET /api/analytics/organization/trends?days=N` — org uptime trend + incident frequency + top offenders (no envelope). */
  getOrganizationTrends: (days?: number) =>
    authedJson<V2OrgTrends>(
      `/api/analytics/organization/trends${days != null ? `?days=${days}` : ''}`,
    ),

  /** `GET /api/billing/subscription` — the org's active subscription (or null) + current plan ({ data, plan }). */
  getSubscription: () => authedJson<V2SubscriptionResponse>('/api/billing/subscription'),

  /** `GET /api/billing/add-ons` — configured add-ons + monthly cost + catalog (402 on the free plan). */
  getAddOns: () => authedJson<V2AddOnsResponse>('/api/billing/add-ons'),

  /** `GET /api/billing/downgrade-preview` — what a downgrade to free would pause (current count + free limit + monitors). */
  getDowngradePreview: () => authedJson<V2DowngradePreview>('/api/billing/downgrade-preview'),

  // ── Billing mutations (Paddle: checkout/cancel/change-plan/portal proxy Paddle externally) ──

  /** `POST /api/billing/add-ons` — set an add-on quantity (0 removes); local DB write, no Paddle. 402 on free. */
  setAddOn: (type: string, quantity: number) =>
    authedJson<{ ok: boolean; monthly_cost_cents: number }>('/api/billing/add-ons', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, quantity }),
    }),

  /**
   * `POST /api/billing/checkout` — create a Paddle checkout; returns the hosted checkout URL.
   * `promoCode` is sent only after `validatePromo` answered valid. The answer carries `promo` when it applied;
   * a bad code is a 422 `promo_invalid` and no Paddle call.
   */
  createCheckout: (plan: string, interval: 'monthly' | 'annual' = 'monthly', promoCode?: string) =>
    authedJson<{
      checkout_url: string
      transaction_id: string
      provider?: string
      promo?: { percent_off: number; duration_intervals: number | null }
    }>('/api/billing/checkout', jsonPost(promoCode ? { plan, interval, promo_code: promoCode } : { plan, interval })),

  /** `GET /api/billing/promo/status` — `enabled:false` (or a 404) when the server flag is off. */
  promoStatus: () => authedJson<{ enabled: boolean }>('/api/billing/promo/status'),

  /** `POST /api/billing/promo/validate` — reserves nothing. 422 `promo_invalid`, 429 with Retry-After. */
  validatePromo: (input: { code: string; plan: string; interval: 'monthly' | 'yearly' }) =>
    authedJson<PromoValidation>('/api/billing/promo/validate', jsonPost(input)),

  /** `POST /api/billing/redeem` — a grant code. 422 `promo_invalid`, 409 `grant_exists`, 429 with Retry-After. */
  redeemCode: (input: { code: string }) => authedJson<RedeemResult>('/api/billing/redeem', jsonPost(input)),

  /** `POST /api/billing/cancel` — cancel the active subscription at period end (Paddle). */
  cancelSubscription: () =>
    authedJson<{ ok: boolean; message: string; access_ends_at?: string | null }>('/api/billing/cancel', { method: 'POST' }),

  /** `POST /api/billing/change-plan` — switch the active subscription to a new plan (Paddle). */
  changePlan: (plan: string, interval?: 'monthly' | 'annual') =>
    authedJson<{ plan: string; interval?: string }>('/api/billing/change-plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(interval ? { plan, interval } : { plan }),
    }),

  /** `POST /api/billing/portal` — create a Paddle customer-portal session; returns the portal URL. */
  createPortalSession: () =>
    authedJson<{ portal_url: string }>('/api/billing/portal', { method: 'POST' }),

  /** `GET /api/auth/2fa/status` — whether TOTP 2FA is enabled for the current user. */
  get2faStatus: () => authedJson<V2TwoFactorStatus>('/api/auth/2fa/status'),

  /** `GET /api/auth/sso/config` — the org's SSO/SAML config ({ data: config|null }). */
  getSsoConfig: () => authedJson<V2SsoConfigResponse>('/api/auth/sso/config'),

  /** `GET /api/custom-sender` — the org's custom email sender ({ data: sender|null }). */
  getCustomSender: () => authedJson<V2CustomSenderResponse>('/api/custom-sender'),

  /** `GET /api/oauth-clients` — the org's OAuth clients, no secrets ({ clients: [...] }). */
  getOAuthClients: () => authedJson<V2OAuthClientsResponse>('/api/oauth-clients'),

  // ── Integration connect (OAuth auth-URL builders + status; no outbound provider call here) ──

  /** `GET /api/integrations/slack/auth` — build the Slack OAuth authorize URL (browser does the redirect). */
  getSlackAuthUrl: () => authedJson<{ auth_url: string }>('/api/integrations/slack/auth'),

  /** `GET /api/integrations/discord/auth` — build the Discord OAuth authorize URL. */
  getDiscordAuthUrl: () => authedJson<{ auth_url: string }>('/api/integrations/discord/auth'),

  /** `GET /api/integrations/telegram/auth` — issue a deep-link + one-time code/state for bot connect. */
  getTelegramAuthUrl: () =>
    authedJson<{ dm_url: string; code: string; state: string }>('/api/integrations/telegram/auth'),

  /** `GET /api/integrations/telegram/status` — poll whether the bot connect completed. */
  getTelegramStatus: (state: string, code: string) =>
    authedJson<{ connected: boolean; channel_id?: string }>(
      `/api/integrations/telegram/status?state=${encodeURIComponent(state)}&code=${encodeURIComponent(code)}`,
    ),

  // ── Misc reads ──

  /** `GET /api/notification-deliveries` — the org's delivery records ({ data } envelope, v2-native). */
  getNotificationDeliveries: (params?: { status?: string; limit?: number }) => {
    const q = new URLSearchParams()
    if (params?.status) q.set('status', params.status)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return authedJson<{ data: V2NotificationDelivery[] }>(
      `/api/notification-deliveries${qs ? `?${qs}` : ''}`,
    )
  },

  /** `GET /api/notification-deliveries/stats?days=N` — status→count over the period. */
  getNotificationDeliveryStats: (days?: number) =>
    authedJson<{ stats: Record<string, number>; period_days: number }>(
      `/api/notification-deliveries/stats${days ? `?days=${days}` : ''}`,
    ),

  /**
   * `POST /api/tools/check-website` — "is this site down?" checker. `opts` lets
   * the create-form Test mirror the monitor's own method/headers/expected codes/
   * verify_ssl so Test and the saved monitor AGREE; the public tool omits them.
   */
  checkWebsite: (url: string, opts?: WebsiteCheckOptions) =>
    authedJson<{ data: V2WebsiteCheck }>('/api/tools/check-website', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, ...opts }),
    }),

  /**
   * Streamed form of `checkWebsite` for the public tool: `onEvent` gets
   * `start`, then one `result` per location as it answers, then `done`.
   * Falls back to the JSON reply (replayed as the same events).
   */
  checkWebsiteStream: async (url: string, onEvent: (ev: WebsiteCheckEvent) => void, signal?: AbortSignal) => {
    const res = await fetch(`${API_URL}/api/tools/check-website`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/x-ndjson, application/json' },
      body: JSON.stringify({ url }),
      credentials: 'include',
      signal,
    })
    if (!res.ok) throw await apiErrorFrom(res)
    if (!res.body || !(res.headers.get('content-type') ?? '').includes('ndjson')) {
      const json = (await res.json()) as { data: V2WebsiteCheck }
      eventsFromJson(json.data).forEach(onEvent)
      return
    }
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      const { lines, rest } = takeLines(buffer + decoder.decode(value, { stream: true }))
      buffer = rest
      for (const line of lines) onEvent(JSON.parse(line) as WebsiteCheckEvent)
    }
    const { lines } = takeLines(buffer + decoder.decode() + '\n')
    for (const line of lines) onEvent(JSON.parse(line) as WebsiteCheckEvent)
  },

  /** `GET /api/public/stats` — global totals for the home page (today and yesterday, UTC). */
  getPublicStats: () => authedJson<{ data: PublicStats }>('/api/public/stats'),

  /**
   * `GET /api/public/free-plan` — `{ state: 'open' | 'full' | 'closed', monitors, interval_seconds, location }`.
   * Public and cacheable: no session cookie. The caller reads the body with `parsePublicFreePlan`.
   */
  getFreePlan: async (signal?: AbortSignal): Promise<unknown> => {
    const res = await fetch(`${API_URL}/api/public/free-plan`, { headers: { Accept: 'application/json' }, signal })
    if (!res.ok) throw await apiErrorFrom(res)
    return res.json()
  },

  /** `POST /api/tools/ssl-check` — public SSL certificate checker (one location, Germany). */
  checkSsl: (host: string, port?: number) =>
    authedJson<{ data: V2SslCheck }>('/api/tools/ssl-check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(port === undefined ? { host } : { host, port }),
    }),

  /** `POST /api/tools/dns-lookup` — public DNS lookup against Cloudflare, Google and Quad9. */
  lookupDns: (name: string, recordType: DnsRecordType, resolvers?: DnsResolverId[]) =>
    authedJson<{ data: V2DnsLookup }>('/api/tools/dns-lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        resolvers && resolvers.length > 0
          ? { name, record_type: recordType, resolvers }
          : { name, record_type: recordType },
      ),
    }),

  /** `POST /api/tools/domain-lookup` — public domain registration lookup (RDAP). */
  lookupDomain: (domain: string) =>
    authedJson<{ data: V2DomainLookup }>('/api/tools/domain-lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain }),
    }),

  /** `GET /api/billing/popular-plan` — the most-popular plan badge ({ plan }). */
  getPopularPlan: () => authedJson<{ plan: string }>('/api/billing/popular-plan'),

  // ── Platform admin (is_admin only; internal admin UI) ──

  /** `POST /api/admin/impersonate` — begin impersonating a target user (session-auth only). */
  startImpersonation: (targetUserId: string) =>
    authedJson<{ ok: boolean; impersonating: { id: string; name: string; email: string; role: string } }>(
      '/api/admin/impersonate',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_user_id: targetUserId }),
      },
    ),

  /** `DELETE /api/admin/impersonate` — stop impersonating; returns the admin summary. */
  stopImpersonation: () =>
    authedJson<{ ok: boolean; user: { id: string; name: string; email: string; role: string } }>(
      '/api/admin/impersonate',
      { method: 'DELETE' },
    ),

  /** `GET /api/admin/users` — paginated platform-wide user search. */
  adminSearchUsers: (query: string, page = 1, perPage = 25, sort: AdminUserSort = 'signed_up_desc') => {
    const params = new URLSearchParams({ q: query, page: String(page), per_page: String(perPage), sort })
    return authedJson<{
      data: Array<{
        id: string
        name: string
        email: string
        role: string
        is_admin: boolean
        organization_id: string
        organization_name: string
        inserted_at: string
        last_signed_in_at: string | null
      }>
      page: number
      per_page: number
      total: number
    }>(`/api/admin/users?${params}`)
  },

  /** `GET /api/admin/notification-health` — platform notification diagnostics. */
  adminGetNotificationHealth: () =>
    authedJson<NotificationHealthResponse>('/api/admin/notification-health'),

  /** `GET /api/admin/notification-deliveries` — paginated platform-wide delivery rows. */
  adminListNotificationDeliveries: (
    filters: { channel_type?: string; status?: string; page?: number; per_page?: number } = {},
  ) => {
    const params = new URLSearchParams()
    if (filters.channel_type) params.set('channel_type', filters.channel_type)
    if (filters.status) params.set('status', filters.status)
    params.set('page', String(filters.page ?? 1))
    params.set('per_page', String(filters.per_page ?? 50))
    return authedJson<{
      data: NotificationDeliveryRow[]
      page: number
      per_page: number
      total: number
    }>(`/api/admin/notification-deliveries?${params}`)
  },

  // ── Nonprofit program (OpenSpec 136) ──

  /** `GET /api/nonprofit/status` — the flag, the grant, the pending or last application, the domain verdicts. */
  getNonprofitStatus: () => authedJson<NonprofitStatus>('/api/nonprofit/status'),

  /** `POST /api/nonprofit/claim` — owner or admin: start the 12-month grant for an eligible domain. */
  claimNonprofit: (domain: string) =>
    authedJson<{ grant: NonprofitGrant; paid_subscription: boolean }>('/api/nonprofit/claim', jsonPost({ domain })),

  /** `POST /api/nonprofit/applications` — owner or admin: apply for a domain that is not listed (201). */
  applyNonprofit: (input: NonprofitApplicationInput) =>
    authedJson<{ application: NonprofitApplication }>('/api/nonprofit/applications', jsonPost(input)),

  /** `GET /api/admin/nonprofit/applications?status=` — platform admin: the application queue. */
  adminListNonprofitApplications: (status: NonprofitApplication['status']) =>
    authedJson<{ data: AdminNonprofitApplication[] }>(
      `/api/admin/nonprofit/applications?${new URLSearchParams({ status })}`,
    ),

  /** `POST /api/admin/nonprofit/applications/{id}/approve` — grants Pro and lists the domain as `manual`. */
  adminApproveNonprofitApplication: (id: string, note?: string) =>
    authedJson<{ grant: NonprofitGrant }>(
      `/api/admin/nonprofit/applications/${encodeURIComponent(id)}/approve`,
      jsonPost(note ? { note } : {}),
    ),

  /** `POST /api/admin/nonprofit/applications/{id}/reject` — the note is emailed to the owners. */
  adminRejectNonprofitApplication: (id: string, note: string) =>
    authedJson<{ application: NonprofitApplication }>(
      `/api/admin/nonprofit/applications/${encodeURIComponent(id)}/reject`,
      jsonPost({ note }),
    ),

  /** `GET /api/admin/nonprofit/grants?status=` — platform admin: grants by status. */
  adminListNonprofitGrants: (status: NonprofitGrant['status']) =>
    authedJson<{ data: AdminNonprofitGrant[] }>(`/api/admin/nonprofit/grants?${new URLSearchParams({ status })}`),

  /** `POST /api/admin/nonprofit/grants` — a direct grant (source `admin`) of 1 to 24 months. */
  adminCreateNonprofitGrant: (input: { organization_id: string; domain: string; months: number; note: string }) =>
    authedJson<{ grant: NonprofitGrant }>('/api/admin/nonprofit/grants', jsonPost(input)),

  /** `POST /api/admin/nonprofit/grants/{id}/revoke` — ends an active grant now; the plan is recomputed. */
  adminRevokeNonprofitGrant: (id: string, reason: string) =>
    authedJson<{ grant: NonprofitGrant }>(
      `/api/admin/nonprofit/grants/${encodeURIComponent(id)}/revoke`,
      jsonPost({ reason }),
    ),

  /** `GET /api/admin/nonprofit/domains?q=` — whitelisted domains (at most 100). */
  adminListNonprofitDomains: (q: string) =>
    authedJson<{ data: AdminNonprofitDomain[] }>(
      `/api/admin/nonprofit/domains${q.trim() ? `?${new URLSearchParams({ q: q.trim() })}` : ''}`,
    ),

  /** `POST /api/admin/nonprofit/domains` — add a curated domain. */
  adminAddNonprofitDomain: (domain: string, note?: string) =>
    authedJson<unknown>('/api/admin/nonprofit/domains', jsonPost(note ? { domain, note } : { domain })),

  /** `DELETE /api/admin/nonprofit/domains/{domain}?source=` — only curated and manual rows can go. */
  adminRemoveNonprofitDomain: (domain: string, source: 'curated' | 'manual') =>
    authedJson<unknown>(
      `/api/admin/nonprofit/domains/${encodeURIComponent(domain)}?${new URLSearchParams({ source })}`,
      { method: 'DELETE' },
    ),

  /** `GET /api/admin/nonprofit/blocks` — blocked domains never qualify. */
  adminListNonprofitBlocks: () => authedJson<{ data: AdminNonprofitBlock[] }>('/api/admin/nonprofit/blocks'),

  /** `POST /api/admin/nonprofit/blocks` — block a domain (a live grant stays until revoked). */
  adminAddNonprofitBlock: (domain: string, reason: string) =>
    authedJson<unknown>('/api/admin/nonprofit/blocks', jsonPost({ domain, reason })),

  /** `DELETE /api/admin/nonprofit/blocks/{domain}`. */
  adminRemoveNonprofitBlock: (domain: string) =>
    authedJson<unknown>(`/api/admin/nonprofit/blocks/${encodeURIComponent(domain)}`, { method: 'DELETE' }),

  /** `GET /api/admin/nonprofit/imports` — the last run of each register import. */
  adminListNonprofitImports: () => authedJson<{ data: AdminNonprofitImport[] }>('/api/admin/nonprofit/imports'),

  /** Build the `/api/analytics/export` CSV download URL (consumed by window.open; not a fetch). */
  getExportUrl: (opts?: { monitorId?: string; start?: string; end?: string }) => {
    const params = new URLSearchParams({ format: 'csv' })
    if (opts?.monitorId) params.set('monitor_id', opts.monitorId)
    if (opts?.start) params.set('start', opts.start)
    if (opts?.end) params.set('end', opts.end)
    return `${API_URL}/api/analytics/export?${params}`
  },

  /** `GET /api/regions` — active probe regions ({ data: [{ id, code, name }] }). */
  getRegions: () =>
    authedJson<{ data: { id: number; code: string; name: string }[] }>('/api/regions'),

  /** `GET /api/account/emails` — the primary address and the extra ones (OpenSpec 126). */
  listAccountEmails: () => authedJson<import('./account-emails').AccountEmails>('/api/account/emails'),

  /** `POST /api/account/emails` — add an address; the server emails a confirm link. A password account sends `password`. */
  addAccountEmail: (data: { email: string; password?: string }) =>
    authedJson<{ ok: boolean; email: string }>('/api/account/emails', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `POST /api/account/emails/confirm` — use the link from the email. No session needed. */
  confirmAccountEmail: (token: string) =>
    authedJson<{ ok: boolean; email: string }>('/api/account/emails/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }),

  /** `POST /api/account/emails/{id}/primary` — make a confirmed address the primary one. */
  makeAccountEmailPrimary: (id: string, password?: string) =>
    authedJson<{ ok: boolean; primary: string }>(`/api/account/emails/${encodeURIComponent(id)}/primary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    }),

  /** `DELETE /api/account/emails/{id}` — remove an extra address. Ends the other sessions. */
  removeAccountEmail: (id: string, password?: string) =>
    authedJson<{ ok: boolean }>(`/api/account/emails/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    }),

  /** `GET /api/edge-areas` — the 11 extra check areas, which are live, and the plan limit. */
  getEdgeAreas: () => authedJson<{ data: import('./edge-areas').EdgeAreasData }>('/api/edge-areas'),

  /** `POST /api/monitors/smart-defaults` — preview smart monitor defaults for a URL ({ data }). */
  getSmartDefaults: (url: string) =>
    authedJson<{ data: V2SmartDefaults }>('/api/monitors/smart-defaults', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    }),

  // ── Monitor mutations ──

  /** `POST /api/monitors` — create a monitor (org-scoped). */
  createMonitor: (data: V2CreateMonitorBody) =>
    authedJson<{ data: EnrichedMonitor }>('/api/monitors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `PATCH /api/monitors/{id}` — partial update of a monitor. */
  updateMonitor: (id: string, data: V2UpdateMonitorBody) =>
    authedJson<{ data: EnrichedMonitor }>(`/api/monitors/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `DELETE /api/monitors/{id}` — delete a monitor (204). */
  deleteMonitor: (id: string) =>
    authedJson<void>(`/api/monitors/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** `POST /api/monitors/bulk` — pause/resume/delete/update_interval across monitors. */
  bulkMonitorAction: (
    action: 'pause' | 'resume' | 'delete' | 'update_interval',
    monitorIds: string[],
    extra?: { interval?: number },
  ) =>
    authedJson<{ data: { count: number; action: string } }>('/api/monitors/bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, monitor_ids: monitorIds, ...extra }),
    }),

  /** `POST /api/monitors/import` — bulk-create monitors from a competitor export. */
  importMonitors: (
    source: string,
    monitors: Array<{ url: string; name?: string; type?: string; interval?: number; keyword?: string }>,
  ) =>
    authedJson<{
      created: number
      skipped: number
      failed: number
      results: Array<{ url: string; name: string; status: 'created' | 'skipped' | 'failed'; reason?: string; id?: string }>
    }>('/api/monitors/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source, monitors }),
    }),

  /** `POST /api/incidents/{id}/explain` — AI root-cause summary (paid-only; cached).
   *  classification: "true_failure" | "misconfiguration" | "insufficient_data" | null. */
  explainIncident: (id: string) =>
    authedJson<{ summary: string; classification: string | null; cached: boolean }>(
      `/api/incidents/${encodeURIComponent(id)}/explain`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      },
    ),

  /** `POST /api/browser-checks/author` — AI-draft a Playwright script for a URL (paid-only). */
  authorBrowserCheck: (url: string, hint?: string) =>
    authedJson<{ script: string }>('/api/browser-checks/author', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, hint }),
    }),

  // ── Alert-channel mutations ──

  /** `POST /api/alert-channels` — create an alert channel (org-scoped). */
  /** `secret` is set once, for a new webhook channel only (its signing secret). */
  createAlertChannel: (data: V2CreateAlertChannelBody) =>
    authedJson<{ data: V2AlertChannel; secret?: string }>('/api/alert-channels', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `POST /api/alert-channels/{id}/test` — send one test alert through this channel only. */
  testAlertChannel: (id: string) =>
    authedJson<{ ok: boolean; error?: string | null }>(`/api/alert-channels/${encodeURIComponent(id)}/test`, { method: 'POST' }),

  /** `PATCH /api/alert-channels/{id}` — partial update of an alert channel. */
  updateAlertChannel: (id: string, data: V2UpdateAlertChannelBody) =>
    authedJson<{ data: V2AlertChannel }>(`/api/alert-channels/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `DELETE /api/alert-channels/{id}` — delete an alert channel (204). */
  deleteAlertChannel: (id: string) =>
    authedJson<void>(`/api/alert-channels/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // ── Status-page mutations ──

  /** `POST /api/status-pages` — create a status page; returns { data: { page, monitors } }. */
  createStatusPage: (data: V2CreateStatusPageBody) =>
    authedJson<Schemas['StatusPageDetailResponse']>('/api/status-pages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `PATCH /api/status-pages/{id}` — partial update; returns { data: { page, monitors } }. */
  updateStatusPage: (id: string, data: V2UpdateStatusPageBody) =>
    authedJson<Schemas['StatusPageDetailResponse']>(`/api/status-pages/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `DELETE /api/status-pages/{id}` — delete a status page (204). */
  deleteStatusPage: (id: string) =>
    authedJson<void>(`/api/status-pages/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // ── Escalation-policy mutations ──

  /** `POST /api/escalation-policies` — create a policy (+ steps); { data: policy }. */
  createEscalationPolicy: (data: V2EscalationPolicyBody) =>
    authedJson<{ data: V2EscalationPolicy }>('/api/escalation-policies', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  // ── On-call schedules ──
  /** `GET /api/on-call-schedules` — the org's on-call rotations. */
  listOnCallSchedules: () =>
    authedJson<{ data: V2OnCallSchedule[] }>('/api/on-call-schedules'),
  /** `POST /api/on-call-schedules` — create a rotation (paid plans only). */
  createOnCallSchedule: (data: V2OnCallScheduleBody) =>
    authedJson<{ data: V2OnCallSchedule }>('/api/on-call-schedules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  /** `DELETE /api/on-call-schedules/{id}`. */
  deleteOnCallSchedule: (id: string) =>
    authedJson<void>(`/api/on-call-schedules/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** `GET /api/on-call-schedules/{id}/current` — who's on call now + when it ends. */
  getOnCallCurrent: (id: string) =>
    authedJson<V2OnCallResolved>(`/api/on-call-schedules/${encodeURIComponent(id)}/current`),

  /** `PATCH /api/escalation-policies/{id}` — update a policy (steps replaced wholesale). */
  updateEscalationPolicy: (id: string, data: V2EscalationPolicyBody) =>
    authedJson<{ data: V2EscalationPolicy }>(`/api/escalation-policies/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `DELETE /api/escalation-policies/{id}` — delete a policy (204). */
  deleteEscalationPolicy: (id: string) =>
    authedJson<void>(`/api/escalation-policies/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // ── Maintenance-window mutations ──

  /** `POST /api/maintenance-windows` — create a maintenance window; { data: window }. */
  createMaintenanceWindow: (data: V2CreateMaintenanceWindowBody) =>
    authedJson<{ data: V2MaintenanceWindow }>('/api/maintenance-windows', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `PATCH /api/maintenance-windows/{id}` — update a window (status='completed' completes it). */
  updateMaintenanceWindow: (id: string, data: V2UpdateMaintenanceWindowBody) =>
    authedJson<{ data: V2MaintenanceWindow }>(`/api/maintenance-windows/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `DELETE /api/maintenance-windows/{id}` — delete a window (204). */
  deleteMaintenanceWindow: (id: string) =>
    authedJson<void>(`/api/maintenance-windows/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // ── API-key mutations ──

  /** `POST /api/api-keys` — create a key; returns the plaintext raw_key ONCE. */
  createApiKey: (data: Schemas['CreateApiKey']) =>
    authedJson<V2CreateApiKeyResponse>('/api/api-keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `POST /api/api-keys/{id}/revoke` — revoke a key ({ api_key, message }). */
  revokeApiKey: (id: string) =>
    authedJson<{ api_key: V2ApiKey; message: string }>(
      `/api/api-keys/${encodeURIComponent(id)}/revoke`,
      { method: 'POST' },
    ),

  /** `DELETE /api/api-keys/{id}` — delete a key ({ success, message }, 200). */
  deleteApiKey: (id: string) =>
    authedJson<{ success: boolean; message: string }>(
      `/api/api-keys/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  // ── Incident mutations (all return { data: { incident, updates } }) ──

  /** `POST /api/incidents` — manually create an incident for a monitor. */
  createIncident: (data: { monitor_id: string; cause?: string }) =>
    authedJson<V2IncidentDetailResponse>('/api/incidents', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `PATCH /api/incidents/{id}` with status='resolved' — resolve an incident. */
  resolveIncident: (id: string) =>
    authedJson<V2IncidentDetailResponse>(`/api/incidents/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'resolved' }),
    }),

  /** `POST /api/incidents/{id}/acknowledge` — acknowledge (pauses escalation). */
  acknowledgeIncident: (id: string) =>
    authedJson<V2IncidentDetailResponse>(
      `/api/incidents/${encodeURIComponent(id)}/acknowledge`,
      { method: 'POST' },
    ),

  /** `POST /api/incidents/{id}/updates` — post a status update to an incident. */
  createIncidentUpdate: (
    incidentId: string,
    data: { title: string; description: string; status: string },
  ) =>
    authedJson<V2IncidentDetailResponse>(
      `/api/incidents/${encodeURIComponent(incidentId)}/updates`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    ),

  // ── Member mutations (org-scoped paths) ──

  /** `PATCH /api/organizations/{orgId}/members/{userId}` — change a member's role. */
  updateMemberRole: (orgId: string, userId: string, role: string) =>
    authedJson<{ data: V2Member }>(
      `/api/organizations/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      },
    ),

  /** `DELETE /api/organizations/{orgId}/members/{userId}` — remove a member (204). */
  removeMember: (orgId: string, userId: string) =>
    authedJson<void>(
      `/api/organizations/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    ),

  // ── Invitation mutations (org-scoped; team-manage required) ──

  /** `POST /api/organizations/{orgId}/invitations` — invite a member by email + role. */
  createInvitation: (orgId: string, data: { email: string; role: string }) =>
    authedJson<{ data: V2Invitation }>(
      `/api/organizations/${encodeURIComponent(orgId)}/invitations`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    ),

  /** `DELETE /api/organizations/{orgId}/invitations/{id}` — cancel a pending invitation (204). */
  cancelInvitation: (orgId: string, id: string) =>
    authedJson<void>(
      `/api/organizations/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  /** `GET /api/invitations/{token}` — public invite-details lookup (no auth; token is the secret). */
  getInvitationByToken: (token: string) =>
    authedJson<{ data: V2PublicInvitation }>(`/api/invitations/${encodeURIComponent(token)}`),

  /** `POST /api/invitations/{token}/accept` — accept (or, if signed-out, get signup details). */
  acceptInvitation: (token: string) =>
    authedJson<{ status: string; data: Record<string, unknown> }>(
      `/api/invitations/${encodeURIComponent(token)}/accept`,
      { method: 'POST' },
    ),

  // ── Auth mutations ──

  /** `GET /api/auth/me` — restore the current session (user + organization). */
  getCurrentUser: () => authedJson<V2CurrentUserResponse>('/api/auth/me'),

  /** `GET /api/auth/providers` — which sign-in methods are enabled. */
  getAuthProviders: () =>
    authedJson<{ providers: { email: boolean; github: boolean; google: boolean } }>(
      '/api/auth/providers',
    ),

  /** `POST /api/auth/logout` — clear the session cookie ({ ok }). */
  logout: () => authedJson<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  /** `POST /api/auth/login` — verify password (+ optional TOTP); sets the session cookie. */
  login: (email: string, password: string, totpCode?: string) =>
    authedJson<V2AuthResponse | V2TotpRequired>('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, ...(totpCode ? { totp_code: totpCode } : {}) }),
    }),

  /** `POST /api/auth/register` — create a user + org; sets the session cookie. */
  register: (name: string, email: string, password: string) =>
    authedJson<V2AuthResponse>('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    }),

  /** `POST /api/auth/magic-link` — issue a passwordless sign-in token (enumeration-safe; email delivered out-of-band). */
  requestMagicLink: (email: string, redirect?: string) =>
    authedJson<{ ok: boolean; message: string }>('/api/auth/magic-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // `redirect` rides in the emailed link so a chosen plan survives
      // opening the email on another device (server re-validates it).
      body: JSON.stringify(redirect ? { email, redirect } : { email }),
    }),

  /** `POST /api/funnel/attribution` — first-touch signup source (first write wins). Internal. */
  postAttribution: (data: Record<string, string | undefined>) =>
    authedJson<{ ok: boolean }>('/api/funnel/attribution', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `POST /api/funnel/events` — client-reported funnel step (server allowlist decides which events it accepts). Internal. */
  postFunnelEvent: (event: FunnelEvent, metadata: Record<string, string> = {}) =>
    authedJson<{ ok: boolean }>('/api/funnel/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, metadata }),
    }),

  /**
   * `POST /api/auth/magic-link/verify-code` — the 6-digit code from the sign-in email, instead of the link.
   * It opens the same one-time token, so the answer is the same as `verifyMagicLink`: a session, or
   * `{ totp_required: true }` first for a 2FA user (send the code again with `totpCode`).
   */
  verifyMagicCode: (email: string, code: string, totpCode?: string) =>
    authedJson<V2AuthResponse | V2TotpRequired>('/api/auth/magic-link/verify-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(totpCode ? { email, code, totp_code: totpCode } : { email, code }),
    }),

  /** `POST /api/auth/magic-link/verify` — consume a magic-link token → session; sets the session cookie. */
  verifyMagicLink: (email: string, token: string, totpCode?: string) =>
    authedJson<V2AuthResponse | V2TotpRequired>('/api/auth/magic-link/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // A 2FA user gets { totp_required: true } first; the page re-POSTs the
      // same link with the code (the token is only consumed on success).
      body: JSON.stringify(totpCode ? { email, token, totp_code: totpCode } : { email, token }),
    }),

  /** `POST /api/auth/sessions/revoke-all` — sign out of every other device. */
  revokeAllSessions: () =>
    authedJson<{ ok: boolean }>('/api/auth/sessions/revoke-all', { method: 'POST' }),

  /** `POST /api/auth/2fa/complete` — finish a Google/GitHub sign-in for a 2FA user. */
  completeTwoFactor: (code: string) =>
    authedJson<{ ok: boolean }>('/api/auth/2fa/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    }),

  /** `PATCH /api/auth/profile` — update the current user's profile (name / locale). */
  updateProfile: (data: { name?: string; preferred_locale?: string }) =>
    authedJson<V2AuthResponse>('/api/auth/profile', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /**
   * `PATCH /api/auth/password` — change the current user's password. An account with no password
   * (`has_password` false) sends the new one only; the server then wants a fresh sign-in (`reauth_required`).
   */
  changePassword: (data: { current_password?: string; new_password: string }) =>
    authedJson<V2AuthResponse>('/api/auth/password', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `DELETE /api/auth/account` — soft-delete the account + org; returns a short-lived goodbye token. */
  deleteAccount: (data: { password?: string }) =>
    authedJson<{ ok: boolean; goodbye_token?: string }>('/api/auth/account', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `POST /api/feedback/goodbye` — anonymous post-deletion feedback (token from deleteAccount). */
  submitGoodbyeFeedback: (data: { token: string; reason?: string; comment?: string }) =>
    authedJson<{ ok: boolean }>('/api/feedback/goodbye', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  // ── 2FA (TOTP) mutations ──

  /** `POST /api/auth/2fa/setup` — start enrollment; returns the secret + otpauth URI. */
  setup2fa: () =>
    authedJson<{ otpauth_uri: string; encoded_secret: string; secret: string }>(
      '/api/auth/2fa/setup',
      { method: 'POST' },
    ),

  /** `POST /api/auth/2fa/confirm` — confirm enrollment with a TOTP code; returns backup codes. */
  confirm2fa: (code: string, secret: string) =>
    authedJson<{ backup_codes: string[] }>('/api/auth/2fa/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, secret }),
    }),

  /** `POST /api/auth/2fa/disable` — disable 2FA with a current TOTP code ({ ok }). */
  disable2fa: (code: string) =>
    authedJson<{ ok: boolean }>('/api/auth/2fa/disable', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    }),

  // ── SSO / SAML mutations (Business-gated -> 402 on lower plans) ──

  /** `POST /api/auth/sso/config` — configure SSO/SAML for the org ({ ok }). */
  configureSso: (config: { entity_id: string; sso_url: string; certificate: string; enforce?: boolean }) =>
    authedJson<{ ok: boolean }>('/api/auth/sso/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    }),

  /** `DELETE /api/auth/sso/config` — remove the org's SSO config ({ ok }). */
  deleteSso: () => authedJson<{ ok: boolean }>('/api/auth/sso/config', { method: 'DELETE' }),

  // ── Custom email sender mutations (Business-gated -> 402 on lower plans) ──

  /** `POST /api/custom-sender` — set up a custom sender ({ ok, message }); sends a verification email. */
  setupCustomSender: (senderName: string, senderEmail: string) =>
    authedJson<{ ok: boolean; message: string }>('/api/custom-sender', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sender_name: senderName, sender_email: senderEmail }),
    }),

  /** `DELETE /api/custom-sender` — remove the org's custom sender ({ ok }). */
  deleteCustomSender: () => authedJson<{ ok: boolean }>('/api/custom-sender', { method: 'DELETE' }),

  // ── OAuth client mutations ──

  /** `POST /api/oauth-clients` — create an OAuth client; returns the client_secret ONCE. */
  createOAuthClient: (data: { name: string; redirect_uris: string[] }) =>
    authedJson<V2CreateOAuthClientOut>('/api/oauth-clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** `DELETE /api/oauth-clients/{id}` — delete an org-owned OAuth client ({ deleted }). */
  deleteOAuthClient: (id: string) =>
    authedJson<{ deleted: boolean }>(`/api/oauth-clients/${encodeURIComponent(id)}`, { method: 'DELETE' }),
}
